import { __awaiter, __rest } from "tslib";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { autoUpdate, flip, offset, useFloating } from '@floating-ui/react';
import { Label, Listbox, ListboxButton, ListboxOptions } from '@headlessui/react';
import classNames from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { createPortal } from 'react-dom';
import { Badge } from '../../../badge/src';
import { isRtl, LocaleContext, useLocalizeMessage } from '../../../i18n/src';
import { Icon } from '../../../icon/src';
import { OPTIONS_SHEET_ENTER_CLASS, OptionsSheetBackdrop, trapOptionsSheetTab, useCompactViewport, useOptionsSheetPressGuard, } from '../../../options_sheet/src';
import { SearchField } from '../../../search_field/src';
import { Tooltip } from '../../../tooltip/src';
import { Text } from '../../../typography/src';
import { SelectOptionItem } from './select_option.component';
import { SelectOptionsSheetHeader } from './select_options_sheet_header.component';
import { SelectOptionsSkeleton } from './select_options_skeleton.component';
import { SelectVirtualOptions } from './select_virtual_options.component';
import { useSelectVirtualKeyboard } from './use_select_virtual_keyboard.hook';
const PLACEMENT_MAP_LTR = {
    bottom: 'bottom-start',
    top: 'top-start',
};
const PLACEMENT_MAP_RTL = {
    bottom: 'bottom-end',
    top: 'top-end',
};
// Distance in pixels from bottom of scroll container to trigger loading more items
const SCROLL_LOAD_THRESHOLD = 50;
const DEFAULT_SEARCH_DEBOUNCE_MS = 250;
const SelectOpenEffects = ({ open, onOpen }) => {
    const prevOpen = useRef(open);
    useEffect(() => {
        if (!prevOpen.current && open) {
            onOpen();
        }
        prevOpen.current = open;
    }, [open, onOpen]);
    return null;
};
const classes = {
    size: {
        xs: 'nucleus-w-input-xs',
        sm: 'nucleus-w-input-sm',
        base: 'nucleus-w-input-base',
        lg: 'nucleus-w-input-lg',
        full: 'nucleus-w-input-full',
    },
    label: 'nucleus-font-medium nucleus-text-sm nucleus-text-text-primary nucleus-flex-none nucleus-order-none nucleus-grow-0 nucleus-block',
    required: 'nucleus-w-1.5 nucleus-h-5 nucleus-font-medium nucleus-text-sm nucleus-text-red-500 nucleus-flex-none nucleus-order-1 nucleus-grow-0 nucleus-pl-px',
    component: {
        common: 'nucleus-border-box nucleus-cursor-pointer nucleus-h-10 nucleus-relative nucleus-border-solid nucleus-border nucleus-w-full nucleus-cursor-default nucleus-rounded-md nucleus-py-2 sm:nucleus-text-sm nucleus-border focus-visible:nucleus-ring-1 focus:nucleus-outline-none focus:nucleus-ring-1 nucleus-text-start nucleus-pl-3 nucleus-pr-2 nucleus-flex nucleus-justify-between nucleus-items-center',
        commonColors: 'nucleus-border-neutral-200 dark:nucleus-border-neutral-300 nucleus-bg-background dark:nucleus-bg-surface nucleus-text-text-primary focus-visible:nucleus-bg-neutral-50 focus-visible:nucleus-text-text-primary focus:nucleus-ring-primary-500 focus:nucleus-border-primary-500 focus-visible:nucleus-ring-primary-500 hover:nucleus-bg-neutral-50 hover:nucleus-text-text-primary',
        disabled: '!nucleus-bg-neutral-200 nucleus-border-neutral-200 nucleus-text-text-disabled nucleus-pointer-events-none nucleus-border-0',
        error: 'nucleus-bg-background dark:nucleus-bg-surface nucleus-border-red-500 nucleus-text-text-primary hover:nucleus-border-red-500 focus:nucleus-ring-red-500 focus:nucleus-border-red-500 focus-visible:nucleus-ring-red-500',
    },
    description: 'nucleus-font-medium nucleus-text-xs nucleus-text-text-secondary nucleus-flex-none nucleus-grow-0 nucleus-mb-2 nucleus-mt-0 nucleus-p-0',
    error: 'nucleus-font-medium nucleus-text-xs nucleus-text-text-error nucleus-order-1 nucleus-grow nucleus-m-0',
    errorContainer: 'nucleus-mt-2 nucleus-flex nucleus-items-start nucleus-gap-2 nucleus-font-sans',
};
/**
 * Select allows a user to select an item from a prefilled list of items.
 * @remarks accessibility best practices for listbox: https://www.w3.org/WAI/ARIA/apg/patterns/listbox/
 */
export const Select = (_a) => {
    var { dataTestId, value, onChange, options, multiple, width = 'base', description, errorMessage, label, selectionLabel, hasError, required, hideRequiredIndicator, disabled, position = 'bottom', displayTooltip = false, displayTooltipOnlyOnOverflow = true, tooltipDelay = 500, placeholder, showMultipleSelectionBadges, searchableOptions, searchPlaceholder, searchableOptionsNoItemsText, onLoadMore, onSearchOptions, searchAsYouType = false, searchDebounceMs = DEFAULT_SEARCH_DEBOUNCE_MS, isLoading = false, useVirtualScrolling = false } = _a, selectProps = __rest(_a, ["dataTestId", "value", "onChange", "options", "multiple", "width", "description", "errorMessage", "label", "selectionLabel", "hasError", "required", "hideRequiredIndicator", "disabled", "position", "displayTooltip", "displayTooltipOnlyOnOverflow", "tooltipDelay", "placeholder", "showMultipleSelectionBadges", "searchableOptions", "searchPlaceholder", "searchableOptionsNoItemsText", "onLoadMore", "onSearchOptions", "searchAsYouType", "searchDebounceMs", "isLoading", "useVirtualScrolling"]);
    const translate = useLocalizeMessage();
    const isCompact = useCompactViewport();
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [sheetScroller, setSheetScroller] = useState(null);
    const searchInputRef = useRef(null);
    const searchDebounceTimeoutRef = useRef(null);
    const cancelPendingDebounce = useCallback(() => {
        if (searchDebounceTimeoutRef.current) {
            clearTimeout(searchDebounceTimeoutRef.current);
            searchDebounceTimeoutRef.current = null;
        }
    }, []);
    const { refs, floatingStyles, elements } = useFloating({
        placement: isRtl(useContext(LocaleContext)) ? PLACEMENT_MAP_RTL[position] : PLACEMENT_MAP_LTR[position],
        whileElementsMounted: isCompact ? undefined : autoUpdate,
        middleware: [offset(4), flip({ crossAxis: false })],
    });
    const filteredOptions = useMemo(() => {
        if (onSearchOptions) {
            return options;
        }
        if (!searchableOptions || !searchTerm.trim()) {
            return options;
        }
        return options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [options, searchTerm, searchableOptions, onSearchOptions]);
    const visibleOptions = useMemo(() => filteredOptions.filter((option) => !option.hidden), [filteredOptions]);
    const isPressGuarded = useOptionsSheetPressGuard(isCompact && Boolean(elements.floating));
    const scrollParent = isCompact ? sheetScroller : elements.floating;
    const { handleKeyDownCapture: handleVirtualKeyDownCapture, revealOption } = useSelectVirtualKeyboard({
        enabled: useVirtualScrolling,
        options: visibleOptions,
        scrollParent,
        searchableOptions,
    });
    const selectionLabelValue = useMemo(() => {
        if (!multiple) {
            return (value === null || value === void 0 ? void 0 : value.label) || placeholder;
        }
        if (selectionLabel) {
            return selectionLabel;
        }
        return value.length > 0 ? translate('Select an option ({ count })', { count: value.length }) : placeholder;
    }, [selectionLabel, value, multiple, placeholder]);
    const removeBadge = useCallback((optionToRemove) => {
        if (multiple) {
            const newValues = value.filter((v) => v.value !== optionToRemove.value);
            onChange(newValues);
        }
    }, [value, multiple, onChange]);
    const handleSearchChange = useCallback((e) => {
        const nextSearchTerm = e.target.value;
        setSearchTerm(nextSearchTerm);
        if (onSearchOptions && searchAsYouType) {
            cancelPendingDebounce();
            searchDebounceTimeoutRef.current = setTimeout(() => {
                onSearchOptions(nextSearchTerm);
                searchDebounceTimeoutRef.current = null;
            }, searchDebounceMs);
        }
    }, [onSearchOptions, searchAsYouType, searchDebounceMs, cancelPendingDebounce]);
    const handleSearchClear = useCallback(() => {
        cancelPendingDebounce();
        setSearchTerm('');
        onSearchOptions === null || onSearchOptions === void 0 ? void 0 : onSearchOptions('');
    }, [onSearchOptions, cancelPendingDebounce]);
    const handleSearchSubmit = useCallback((term) => {
        cancelPendingDebounce();
        onSearchOptions === null || onSearchOptions === void 0 ? void 0 : onSearchOptions(term);
    }, [onSearchOptions, cancelPendingDebounce]);
    const handleSearchKeyDown = useCallback((e) => {
        const target = e.target;
        if (target === searchInputRef.current) {
            if (e.key === 'Enter' && onSearchOptions) {
                handleSearchSubmit(searchInputRef.current.value);
            }
            e.stopPropagation();
        }
    }, [onSearchOptions, handleSearchSubmit]);
    const handleListboxKeyDownCapture = useCallback((e) => {
        if (isCompact && trapOptionsSheetTab(e)) {
            return;
        }
        handleSearchKeyDown(e);
        handleVirtualKeyDownCapture(e);
    }, [handleSearchKeyDown, handleVirtualKeyDownCapture, isCompact]);
    const handleOpen = useCallback(() => {
        var _a, _b;
        cancelPendingDebounce();
        setSearchTerm('');
        onSearchOptions === null || onSearchOptions === void 0 ? void 0 : onSearchOptions('');
        // A compact sheet that focuses its search field opens the soft keyboard over the list.
        if (searchableOptions && !isCompact) {
            (_a = searchInputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
        revealOption(multiple ? (_b = value[0]) === null || _b === void 0 ? void 0 : _b.value : value === null || value === void 0 ? void 0 : value.value);
    }, [onSearchOptions, searchableOptions, isCompact, cancelPendingDebounce, multiple, value, revealOption]);
    useEffect(() => {
        if (scrollParent) {
            scrollParent.scrollTop = 1; // Trigger the scrollbar to appear
            scrollParent.scrollTop = 0; // Reset to original position
        }
    }, [scrollParent]);
    const loadMore = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!onLoadMore || isLoadingMore) {
            return;
        }
        setIsLoadingMore(true);
        try {
            yield onLoadMore();
        }
        finally {
            setIsLoadingMore(false);
        }
    }), [onLoadMore, isLoadingMore]);
    /**
     * Handles the scroll event for the associated element, and determine whether
     * a "load more" action should be triggered
     */
    const handleScroll = useCallback((event) => {
        if (!onLoadMore || isLoadingMore) {
            return;
        }
        const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
        handleLoadMore(scrollHeight - scrollTop, clientHeight);
    }, [onLoadMore, isLoadingMore]);
    /**
     * Identifies cases where the container has no scroll but new pages are available. The virtualized list reports the
     * end of the list on its own, so this only applies to the non-virtualized options.
     */
    useEffect(() => {
        if (useVirtualScrolling || !onLoadMore || isLoadingMore || !scrollParent) {
            return;
        }
        const rafId = requestAnimationFrame(() => {
            if (!onLoadMore || scrollParent.clientHeight === 0) {
                return;
            }
            handleLoadMore(scrollParent.scrollHeight, scrollParent.clientHeight);
        });
        return () => cancelAnimationFrame(rafId);
    }, [filteredOptions, onLoadMore, isLoadingMore, scrollParent, useVirtualScrolling]);
    useEffect(() => {
        return cancelPendingDebounce;
    }, [cancelPendingDebounce]);
    const handleLoadMore = (scrollHeight, clientHeight) => {
        if (scrollHeight <= clientHeight + SCROLL_LOAD_THRESHOLD) {
            loadMore();
        }
    };
    const optionsContent = (_jsxs(_Fragment, { children: [useVirtualScrolling
                ? scrollParent && (_jsx(SelectVirtualOptions, { options: visibleOptions, multiple: multiple, scrollParent: scrollParent, onLoadMore: onLoadMore && loadMore, hasNextPage: !!onLoadMore, isLoadingMore: isLoadingMore, isSheet: isCompact }))
                : visibleOptions.map((option) => (_jsx(SelectOptionItem, { option: option, multiple: multiple, isSheet: isCompact }, option.value))), !isLoading && visibleOptions.length === 0 && (_jsx("div", { className: 'nucleus-pointer-events-none nucleus-px-3 nucleus-py-2', children: _jsx(Text, { size: 'sm', className: 'nucleus-text-text-secondary', children: searchableOptionsNoItemsText || translate('No options found') }) })), (isLoading || (!useVirtualScrolling && onLoadMore && isLoadingMore)) && _jsx(SelectOptionsSkeleton, { rows: 2 })] }));
    return (_jsxs("div", { className: `${classes.size[width]} nucleus-inline-block`, children: [_jsx(Listbox, Object.assign({ multiple: multiple, value: value, onChange: onChange, by: 'value', as: 'div', className: 'nucleus-relative nucleus-font-sans', disabled: disabled, "aria-label": label, "aria-required": required, "data-testid": dataTestId }, selectProps, { children: ({ open }) => {
                    var _a;
                    return (_jsxs(_Fragment, { children: [_jsx(SelectOpenEffects, { open: open, onOpen: handleOpen }), label && (_jsxs(Label, { className: classNames(classes.label, classes.size[width], !description && 'nucleus-mb-2'), children: [label, required && !hideRequiredIndicator && _jsx("span", { className: classes.required, children: "*" })] })), description && _jsx("p", { className: classNames(classes.description, classes.size[width]), children: description }), _jsxs(ListboxButton, { className: classNames(classes.component.common, {
                                    [classes.component.disabled]: disabled,
                                    [classes.component.error]: hasError,
                                    [classes.component.commonColors]: !disabled && !hasError && !open,
                                    'nucleus-bg-primary-100 dark:nucleus-bg-primary-800 nucleus-text-text-primary nucleus-border-0': open,
                                }), ref: refs.setReference, "aria-disabled": !!disabled, children: [displayTooltip ? (_jsx(Tooltip, { title: selectionLabelValue, wrapperClassName: 'nucleus-truncate nucleus-text-sm nucleus-inline', as: 'span', delayShow: tooltipDelay, displayOnlyOnOverflow: displayTooltipOnlyOnOverflow, children: selectionLabelValue })) : (_jsx("span", { className: 'nucleus-truncate', children: selectionLabelValue })), _jsxs("div", { className: 'nucleus-ml-auto', children: [open && _jsx(Icon, { name: 'chevronUp', color: 'grayDark', size: 'sm' }), !open && _jsx(Icon, { name: 'chevronDown', color: 'gray', size: 'sm' })] })] }), createPortal(_jsxs(_Fragment, { children: [open && isCompact && _jsx(OptionsSheetBackdrop, { isPressGuarded: isPressGuarded }), _jsxs(ListboxOptions, { "data-testid": isCompact ? 'nucleus-options-sheet' : undefined, "data-presentation": isCompact ? 'sheet' : 'popover', className: isCompact
                                            ? `nucleus-fixed nucleus-inset-x-0 nucleus-bottom-0 nucleus-z-[70] nucleus-m-0 nucleus-box-border nucleus-flex nucleus-max-h-[min(36rem,85dvh)] nucleus-w-full nucleus-list-none nucleus-flex-col nucleus-overflow-hidden nucleus-rounded-t-3xl nucleus-border nucleus-border-x-0 nucleus-border-b-0 nucleus-border-solid nucleus-border-neutral-200 dark:nucleus-border-neutral-300 nucleus-bg-background dark:nucleus-bg-surface nucleus-font-sans nucleus-text-base nucleus-shadow-xl nucleus-px-0 nucleus-pb-[max(0.75rem,env(safe-area-inset-bottom))] nucleus-pt-0 focus:nucleus-outline-none ${OPTIONS_SHEET_ENTER_CLASS} ${isPressGuarded ? 'nucleus-pointer-events-none' : ''}`
                                            : 'nucleus-absolute nucleus-z-50 nucleus-max-h-60 nucleus-list-none nucleus-overflow-y-auto nucleus-overflow-x-hidden nucleus-bg-background dark:nucleus-bg-surface nucleus-border nucleus-border-solid nucleus-border-neutral-200 dark:nucleus-border-neutral-300 nucleus-font-sans nucleus-text-base nucleus-shadow-xl nucleus-m-0 nucleus-rounded-md nucleus-px-0 nucleus-py-1 focus:nucleus-outline-none sm:nucleus-text-sm', ref: refs.setFloating, style: isCompact
                                            ? undefined
                                            : Object.assign(Object.assign({}, floatingStyles), { width: (_a = elements.reference) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().width, ['--shadow-ring-strength']: 0 }), onScroll: isCompact || useVirtualScrolling ? undefined : handleScroll, onKeyDownCapture: handleListboxKeyDownCapture, children: [isCompact && _jsx(SelectOptionsSheetHeader, { label: label }), searchableOptions && (_jsx("div", { className: isCompact ? 'nucleus-shrink-0 nucleus-px-4 nucleus-pb-2' : 'nucleus-p-2', children: _jsx(SearchField, { id: 'select-search', autoComplete: 'off', ref: searchInputRef, ariaLabel: translate('Search options'), placeholder: searchPlaceholder || translate('Search options...'), value: searchTerm, onChange: handleSearchChange, onClear: handleSearchClear, dataTestId: `${dataTestId}-search-options`, width: 'full' }) })), isCompact ? (_jsx("div", { ref: setSheetScroller, className: 'nucleus-min-h-0 nucleus-flex-1 nucleus-overflow-x-hidden nucleus-overflow-y-auto nucleus-py-1', onScroll: useVirtualScrolling ? undefined : handleScroll, children: optionsContent })) : (optionsContent)] })] }), document.body)] }));
                } })), hasError && errorMessage && (_jsxs("div", { className: classNames(classes.size[width], classes.errorContainer), children: [_jsx("span", { className: 'nucleus-flex nucleus-shrink-0', children: _jsx(Icon, { name: 'errorSolid', color: 'red', size: 'sm' }) }), _jsx("p", { className: classes.error, role: 'alert', children: errorMessage })] })), multiple && showMultipleSelectionBadges && value && value.length > 0 && (_jsx("div", { className: classNames(classes.size[width], 'nucleus-mt-2 nucleus-flex nucleus-flex-wrap nucleus-gap-2'), children: value.map((selectedOption) => (_jsx(Badge, { variant: 'common', label: selectedOption.label, endIcon: {
                        name: 'close',
                        size: 'sm',
                        onClick: () => {
                            removeBadge(selectedOption);
                        },
                    } }, selectedOption.value))) }))] }));
};
//# sourceMappingURL=select.component.js.map